import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import Modal from '../../components/Modal';
import { Tag } from '../../types';
import './AdminTags.css';

interface TagFormData {
  name: string;
  type: string;
}

const AdminTags: React.FC = () => {
  const { success, error: showError } = useToast();
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagTypes, setTagTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
    type: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null }>({ show: false, id: null });

  const fetchTags = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Tag[]>('/admin/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      showError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchTagTypes = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<string[]>('/admin/tags/types');
      setTagTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch tag types:', error);
    }
  }, []);

  useEffect(() => {
    fetchTags();
    fetchTagTypes();
  }, [fetchTags, fetchTagTypes]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/tags/${editingId}`, formData);
        success('Tag updated successfully');
      } else {
        await api.post('/admin/tags', formData);
        success('Tag created successfully');
      }
      handleCloseModal();
      fetchTags();
    } catch (error: any) {
      console.error('Failed to save tag:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save tag';
      showError(errorMessage);
    }
  };

  const handleEdit = (tag: Tag): void => {
    setEditingId(tag.id);
    setFormData({
      name: tag.name,
      type: tag.type || ''
    });
    setShowModal(true);
  };

  const handleDelete = (id: number): void => {
    setDeleteConfirm({ show: true, id });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/admin/tags/${deleteConfirm.id}`);
      success('Tag deleted successfully');
      fetchTags();
      setDeleteConfirm({ show: false, id: null });
    } catch (error: any) {
      console.error('Failed to delete tag:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete tag';
      showError(errorMessage);
      setDeleteConfirm({ show: false, id: null });
    }
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ name: '', type: '' });
  };

  const tagsByType = tags.reduce((acc, tag) => {
    const type = tag.type || 'Uncategorized';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(tag);
    return acc;
  }, {} as Record<string, Tag[]>);

  if (loading) {
    return <div className="admin-tags">Loading...</div>;
  }

  return (
    <div className="admin-tags">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Manage Tags</h1>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            <i className="fas fa-plus"></i> Add Tag
          </button>
        </div>

        <div className="tags-by-type">
          {Object.entries(tagsByType).map(([type, typeTags]) => (
            <div key={type} className="tag-type-section">
              <h2>{type}</h2>
              <div className="tags-grid">
                {typeTags.map((tag) => (
                  <div key={tag.id} className="tag-card">
                    <div className="tag-info">
                      <span className="tag-name">{tag.name}</span>
                      <span className="tag-type-badge">{tag.type}</span>
                    </div>
                    <div className="tag-actions-inline">
                      <button
                        onClick={() => handleEdit(tag)}
                        className="icon-button"
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(tag.id)}
                        className="icon-button icon-button-danger"
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {tags.length === 0 && (
            <div className="no-tags">
              <p>No tags found. Create your first tag to get started.</p>
            </div>
          )}
        </div>

        {showModal && (
          <Modal
            isOpen={showModal}
            onClose={handleCloseModal}
            title={editingId ? 'Edit Tag' : 'Add Tag'}
            size="small"
          >
            <form onSubmit={handleSubmit} className="tag-form">
              <div className="form-group">
                <label>Tag Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Abstract, Landscape, Happy"
                />
              </div>

              <div className="form-group">
                <label>Tag Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">Select a type...</option>
                  {tagTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </Modal>
        )}

        <Modal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          title="Confirm Delete"
          size="small"
        >
          <p>Are you sure you want to delete this tag? This action cannot be undone.</p>
          <p className="help-text">Note: Tags that are assigned to artworks cannot be deleted.</p>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm({ show: false, id: null })}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={confirmDelete}>
              Delete
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AdminTags;
